import React, { useState } from "react";
import { Switch } from "@emisgroup/ui-switch";
import { useTranslation } from "@emisgroup/acp-application-intl";
import { useSelector } from "react-redux";
import { IRootState } from "../../../../reducer/CombineReducer";
import { IScheduleInstanceType, SchedulingModes } from "../CreateSchedule/redux/Types";
import "./Style.scss";

/**
 * Toggle button props
 */
interface ToggleButtonProps {
  /**
   * Selected value on/off
   */
  isSelected: boolean;
  /**
   * To disble switch
   */
  disabled?: boolean;
  /**
   * Method to handle toggle switch
   * @param selectedValue on/off
   * @returns value selected
   */
  onToggle?: (selectedValue: boolean) => void;
}

/**
 * This component is responsible for rendering the toggle button in the schedule configuration
 * @param props isselected to check the toggle is on or off, disabled to make toggle disable and enable
 * @returns JSX element
 */
export const ToggleButton: React.FC<ToggleButtonProps> = (props: ToggleButtonProps): JSX.Element => {
  const { t: translate } = useTranslation();
  const scheduling = useSelector((state: IRootState) => state.scheduling);
  const { isSelected, disabled, onToggle } = props;
  const [selected, setSelected] = useState(isSelected);
  const { scheduleInstances } = scheduling;
  const isCreateMode = scheduling.mode === SchedulingModes.CREATE;

  /**
   * Renders the toggle with the selected option
   */
  React.useEffect(() => {
    setSelected(isSelected);
  }, [isSelected]);

  /**
   * Renders when schedule instance gets updated
   */
  React.useEffect(() => {
    if (!isCreateMode) {
      const activeOrInactiveInstance = scheduleInstances.some((Instance: IScheduleInstanceType) => {
        return Instance.isActive === true;
      });
      setSelected(activeOrInactiveInstance);
    }
  }, [scheduleInstances]);

  /**
   * Onchange event of the toggle button
   * @param selectedValue on/off
   */
  const getToggleText = (selectedValue: boolean): void => {
    setSelected(selectedValue);
    if (onToggle) {
      onToggle(selectedValue);
    }
  };

  return (
    <div>
      <Switch isSelected={selected} onChange={getToggleText} isDisabled={disabled}>
        <span className="ebt-schedule-switch">
          {selected ?
            translate("Scheduling.Heading.ScheduleActive") : translate("Scheduling.Heading.ScheduleNotActive")
          }
        </span>
      </Switch>
    </div>

  );
};
